import {
    Devis,
    FactureFormalite,
    Prescripteur,
    useFacturationService,
    useFormaliteService,
    useSociete,
    useTiersService,
} from '@europrocurement/l2d-domain';
import {
    DevisFormalitApiApiDevisFormaliteGetCollectionRequest,
    FactureApiApiFacturesGetCollectionRequest,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { filtersToElasticQuery } from '@europrocurement/l2d-redux-utils';
import {
    SET_CURRENT_DOSSIER_INVOICED_CTX,
    useCurrentDossierCtxDispatch,
} from '../modules/Dossiers/providers/CurrentDossierProvider';
import {
    INIT_ITEMS_CTX,
    useDossierValidatorCtxDispatch,
    useDossierValidatorsCtx,
} from '../providers/DossierValidatorsProvider';
import {
    ADD_TIERS_CTX,
    apiToStateTransformer as tiersApiToStateTransformer,
    START_FETCH_TIERS_CTX,
    STOP_FETCH_TIERS_CTX,
    useTiersCtx,
    useTiersCtxDispatch,
    ADD_MANY_TIERS_CTX,
} from '../providers/TiersProvider';
import {
    INIT_COLLECTION_INVOICE_CTX,
    collectionInvoiceApiToStateTransformer,
    START_LOAD_COLLECTION_INVOICE_CTX,
    STOP_LOAD_COLLECTION_INVOICE_CTX,
    START_FETCH_COLLECTION_INVOICE_CTX,
    STOP_FETCH_COLLECTION_INVOICE_CTX,
    INIT_COLLECTION_ESTIMATE_CTX,
    START_LOAD_COLLECTION_ESTIMATE_CTX,
    collectionEstimateApiToStateTransformer,
    STOP_LOAD_COLLECTION_ESTIMATE_CTX,
    START_FETCH_COLLECTION_ESTIMATE_CTX,
    STOP_FETCH_COLLECTION_ESTIMATE_CTX,
    useCollectionBillingCtxDispatch,
    INIT_COLLECTION_CTX,
    collectionApiToStateTransformer,
    SET_DATA_PREFACTURATION_CTX,
    START_LOAD_DATA_PREFACTURATION_CTX,
    STOP_LOAD_DATA_PREFACTURATION_CTX,
    SET_BILLING_DOSSIER_IDS_CTX,
    START_LOAD_BILLING_DOSSIER_IDS_CTX,
    STOP_LOAD_BILLING_DOSSIER_IDS_CTX,
    SET_ACOMPTES_CTX,
    START_FETCH_ACOMPTES_CTX,
    STOP_FETCH_ACOMPTES_CTX,
    SET_REGLEMENTS_CTX,
    START_FETCH_REGLEMENTS_CTX,
    STOP_FETCH_REGLEMENTS_CTX,
    START_FETCH_DATA_PREFACTURATION_CTX,
    START_FETCH_BILLING_DOSSIER_IDS_CTX,
    STOP_FETCH_DATA_PREFACTURATION_CTX,
    STOP_FETCH_BILLING_DOSSIER_IDS_CTX,
    dataPrefacturationApiToStateTransformer,
    collectionProvisionToAcompte,
    collectionReglementToReglement,
} from '../providers/CollectionBillingProvider';
import useDossierPrefacturation from '../modules/Dossiers/hooks/useDossierPrefacturation';
import useCollectionProvision from '../modules/Dossiers/hooks/useCollectionProvision';
import useCollectionReglement from '../modules/Dossiers/hooks/useCollectionReglement';

const useLazyDataLoader = () => {
    // data providers hooks
    const { isDataLoaded: validatorsDataLoaded } = useDossierValidatorsCtx();
    const { isFetch } = useTiersCtx();

    // dispatch actions hooks
    const validatorsDispatch = useDossierValidatorCtxDispatch();
    const tiersDispatch = useTiersCtxDispatch();
    const billingDispatch = useCollectionBillingCtxDispatch();
    const currentDossierDispatch = useCurrentDossierCtxDispatch();

    // api hooks
    const { validatorsApi, collectionOpenSearchApi } = useFormaliteService();
    const { prescribersApi, sousClientsApi } = useTiersService();
    const { factureApi, devisFormaliteApi } = useFacturationService();

    // other hooks
    const xIdSociete = useSociete();

    // Dossier validators
    const loadValidators = async (): Promise<void> => {
        if (validatorsDataLoaded() || !validatorsDispatch) {
            return Promise.resolve();
        }

        const response = await validatorsApi.apiDossiersValideursGetCollection({
            itemsPerPage: 999,
        });
        validatorsDispatch({
            type: INIT_ITEMS_CTX,
            payload: response.data['hydra:member'],
        });

        return Promise.resolve();
    };

    // Tiers
    const loadTiers = async (tiersId: number, force: boolean = false): Promise<void> => {
        if (!tiersDispatch || (isFetch(tiersId) && !force)) {
            return Promise.resolve();
        }

        tiersDispatch({
            type: START_FETCH_TIERS_CTX,
            payload: tiersId,
        });

        const tiersResponse = await prescribersApi.getTiersTiersItem({
            id: tiersId as unknown as string,
            xIdSociete,
        });
        const tiers = tiersResponse.data as Prescripteur;

        if (tiers.isPrescripteur) {
            const query = filtersToElasticQuery({
                isSousclient: true,
                idPrescripteur: tiersId,
            });
            const sousClientResponse = await sousClientsApi.getListTiersTiersCollection({
                xIdSociete,
                search: query,
                itemsPerPage: 1000,
            });

            tiersDispatch({
                type: ADD_MANY_TIERS_CTX,
                payload: [tiers, ...sousClientResponse.data['hydra:member']].map((item) =>
                    tiersApiToStateTransformer(item as Prescripteur, xIdSociete),
                ),
            });
        } else {
            tiersDispatch({
                type: ADD_TIERS_CTX,
                payload: tiersApiToStateTransformer(tiers, xIdSociete),
            });
        }

        tiersDispatch({
            type: STOP_FETCH_TIERS_CTX,
            payload: tiersId,
        });

        return Promise.resolve();
    };

    const refreshTiers = async (tiersId: number): Promise<void> => {
        if (!tiersDispatch) {
            return Promise.resolve();
        }

        return loadTiers(tiersId, true);
    };

    // Collection invoices
    const loadCollectionInvoices = async (collectionIds: Array<number>): Promise<void> => {
        if (!billingDispatch) {
            console.log('Dispatcher not available');
            return Promise.resolve();
        }

        billingDispatch({
            type: START_LOAD_COLLECTION_INVOICE_CTX,
        });

        const requestParameters: FactureApiApiFacturesGetCollectionRequest = {
            xIdSociete,
            collectionsId2: collectionIds,
        };

        const response = await factureApi.apiFacturesGetCollection(requestParameters);
        const data = response.data['hydra:member'] as FactureFormalite[];

        billingDispatch({
            type: INIT_COLLECTION_INVOICE_CTX,
            payload: data.map(collectionInvoiceApiToStateTransformer),
        });

        billingDispatch({
            type: STOP_LOAD_COLLECTION_INVOICE_CTX,
        });

        return Promise.resolve();
    };

    // Collection estimates
    const loadCollectionEstimates = async (collectionIds: Array<number>): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }
        billingDispatch({
            type: START_LOAD_COLLECTION_ESTIMATE_CTX,
        });
        const requestParameters: DevisFormalitApiApiDevisFormaliteGetCollectionRequest = {
            xIdSociete,
            collectionsId2: collectionIds,
        };

        const response = await devisFormaliteApi.apiDevisFormaliteGetCollection(requestParameters);
        const data = response.data['hydra:member'] as Devis[];

        billingDispatch({
            type: INIT_COLLECTION_ESTIMATE_CTX,
            payload: data.map(collectionEstimateApiToStateTransformer),
        });
        billingDispatch({
            type: STOP_LOAD_COLLECTION_ESTIMATE_CTX,
        });
        return Promise.resolve();
    };

    // Collection invoices refresh
    const refreshCollectionInvoices = async (collectionIds: Array<number>): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }

        billingDispatch({ type: START_FETCH_COLLECTION_INVOICE_CTX });

        const requestParameters: FactureApiApiFacturesGetCollectionRequest = {
            xIdSociete,
            collectionsId2: collectionIds,
        };

        const response = await factureApi.apiFacturesGetCollection(requestParameters);
        const data = response.data['hydra:member'] as FactureFormalite[];

        billingDispatch({
            type: INIT_COLLECTION_INVOICE_CTX,
            payload: data.map(collectionInvoiceApiToStateTransformer),
        });

        billingDispatch({ type: STOP_FETCH_COLLECTION_INVOICE_CTX });

        return Promise.resolve();
    };

    // Collection estimates refresh
    const refreshCollectionEstimates = async (collectionIds: Array<number>): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }

        billingDispatch({ type: START_FETCH_COLLECTION_ESTIMATE_CTX });

        const requestParameters: DevisFormalitApiApiDevisFormaliteGetCollectionRequest = {
            xIdSociete,
            collectionsId2: collectionIds,
        };

        const response = await devisFormaliteApi.apiDevisFormaliteGetCollection(requestParameters);
        const data = response.data['hydra:member'] as Devis[];

        billingDispatch({
            type: INIT_COLLECTION_ESTIMATE_CTX,
            payload: data.map(collectionEstimateApiToStateTransformer),
        });

        billingDispatch({ type: STOP_FETCH_COLLECTION_ESTIMATE_CTX });

        return Promise.resolve();
    };

    // Dossier prefacturation
    const { getDossiersFacturables, searchDossierArticlesEtReductions } =
        useDossierPrefacturation();
    const { searchCollectionProvision } = useCollectionProvision();
    const { searchCollectionReglement } = useCollectionReglement();

    const loadDossierPrefacturation = async (dossierIds: Array<number>): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }

        billingDispatch({ type: START_LOAD_DATA_PREFACTURATION_CTX });
        billingDispatch({ type: START_LOAD_BILLING_DOSSIER_IDS_CTX });

        const dossiersFacturables = await getDossiersFacturables(dossierIds);
        const data = await searchDossierArticlesEtReductions(dossierIds, dossiersFacturables);

        billingDispatch({
            type: SET_DATA_PREFACTURATION_CTX,
            payload: dataPrefacturationApiToStateTransformer(data),
        });

        billingDispatch({
            type: SET_BILLING_DOSSIER_IDS_CTX,
            payload: dossiersFacturables,
        });

        billingDispatch({ type: STOP_LOAD_DATA_PREFACTURATION_CTX });
        billingDispatch({ type: STOP_LOAD_BILLING_DOSSIER_IDS_CTX });

        return Promise.resolve();
    };

    const refreshDossierPrefacturation = async (
        dossierIds: Array<number>,
        setDossiersFacturables: boolean = false,
        selectedIds?: Array<number>,
    ): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }

        billingDispatch({ type: START_FETCH_DATA_PREFACTURATION_CTX });
        billingDispatch({ type: START_FETCH_BILLING_DOSSIER_IDS_CTX });

        const dossiersFacturables = await getDossiersFacturables(dossierIds);
        const idsSelected = selectedIds ?? dossiersFacturables;
        const data = await searchDossierArticlesEtReductions(dossierIds, idsSelected);

        billingDispatch({
            type: SET_DATA_PREFACTURATION_CTX,
            payload: dataPrefacturationApiToStateTransformer(data),
        });

        if (setDossiersFacturables) {
            billingDispatch({
                type: SET_BILLING_DOSSIER_IDS_CTX,
                payload: dossiersFacturables,
            });
        }

        if (currentDossierDispatch) {
            const hasBilledArticle =
                data.dossiers?.some((dossier) =>
                    dossier.articles?.some((article) => article.articleFacture),
                ) ?? false;
            currentDossierDispatch({
                type: SET_CURRENT_DOSSIER_INVOICED_CTX,
                payload: hasBilledArticle,
            });
        }

        billingDispatch({ type: STOP_FETCH_DATA_PREFACTURATION_CTX });
        billingDispatch({ type: STOP_FETCH_BILLING_DOSSIER_IDS_CTX });

        return Promise.resolve();
    };

    const loadCollectionProvision = async (collectionIds: number[]): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }

        billingDispatch({ type: START_FETCH_ACOMPTES_CTX });

        const data = await searchCollectionProvision(collectionIds);

        billingDispatch({
            type: SET_ACOMPTES_CTX,
            payload: data.map(collectionProvisionToAcompte),
        });
        billingDispatch({ type: STOP_FETCH_ACOMPTES_CTX });

        return Promise.resolve();
    };

    const loadCollectionReglement = async (collectionIds: number[]): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }

        billingDispatch({ type: START_FETCH_REGLEMENTS_CTX });

        const data = await searchCollectionReglement(collectionIds);

        billingDispatch({
            type: SET_REGLEMENTS_CTX,
            payload: data.map(collectionReglementToReglement),
        });
        billingDispatch({ type: STOP_FETCH_REGLEMENTS_CTX });

        return Promise.resolve();
    };

    // Collections
    const loadCollections = async (collectionIds: Array<number>): Promise<void> => {
        if (!billingDispatch) {
            return Promise.resolve();
        }

        const responses = await Promise.all(
            collectionIds.map((id) =>
                collectionOpenSearchApi.apiOpensearchCollectionsIdGet({ id: String(id) }),
            ),
        );

        billingDispatch({
            type: INIT_COLLECTION_CTX,
            payload: responses.map((res) => collectionApiToStateTransformer(res.data)),
        });

        return Promise.resolve();
    };

    return {
        loadValidators,
        loadTiers,
        refreshTiers,
        loadCollectionInvoices,
        refreshCollectionInvoices,
        refreshCollectionEstimates,
        loadCollectionEstimates,
        loadCollections,
        loadDossierPrefacturation,
        loadCollectionProvision,
        loadCollectionReglement,
        refreshDossierPrefacturation,
    };
};

export default useLazyDataLoader;
